import { useMemo, useState } from "react";
import cls from "./CreateGameForm.module.scss";

export default function CreateGameForm({ onSubmit, loading = false }) {
  const [form, setForm] = useState({
    title: "",
    slug: "",
    joinName: "",
    password: "",
    status: "draft",
    maxRounds: 4,
    allowSelfieUpload: true,
    avatarSetKey: "default",
    settings: {
      coinCostPerLoss: 1,
      afterRound1CoinsDisabled: true,
    },
  });

  const isDisabled = useMemo(() => loading, [loading]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const updateSetting = (key, value) => {
    setForm((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        [key]: value,
      },
    }));
  };

  const submit = (e) => {
    e.preventDefault();

    onSubmit({
      ...form,
      maxRounds: Number(form.maxRounds),
      settings: {
        coinCostPerLoss: Number(form.settings.coinCostPerLoss),
        afterRound1CoinsDisabled: Boolean(
          form.settings.afterRound1CoinsDisabled,
        ),
      },
    });
  };

  return (
    <form className={cls.formCard} onSubmit={submit}>
      <div className={cls.bgGlow} aria-hidden="true" />
      <div className={cls.bgGlowTwo} aria-hidden="true" />

      <div className={cls.header}>
        <div className={cls.kicker}>ADMIN PANEL</div>
        <h2 className={cls.title}>Create New Game</h2>
        <p className={cls.sub}>
          Build a new game room with secure join access and player settings.
        </p>
      </div>

      <div className={cls.grid}>
        <label className={cls.field}>
          <span className={cls.label}>Game Title</span>
          <input
            className={cls.input}
            type="text"
            placeholder="Enter game title"
            value={form.title}
            onChange={(e) => updateField("title", e.target.value)}
            required
          />
        </label>

        <label className={cls.field}>
          <span className={cls.label}>Slug</span>
          <input
            className={cls.input}
            type="text"
            placeholder="game-slug"
            value={form.slug}
            onChange={(e) => updateField("slug", e.target.value)}
            required
          />
        </label>

        <label className={cls.field}>
          <span className={cls.label}>Join Name</span>
          <input
            className={cls.input}
            type="text"
            placeholder="Unique join name"
            value={form.joinName}
            onChange={(e) => updateField("joinName", e.target.value)}
            required
          />
        </label>

        <label className={cls.field}>
          <span className={cls.label}>Password</span>
          <input
            className={cls.input}
            type="password"
            placeholder="Enter game password"
            value={form.password}
            onChange={(e) => updateField("password", e.target.value)}
            required
          />
        </label>

        <label className={cls.field}>
          <span className={cls.label}>Status</span>
          <select
            className={cls.select}
            value={form.status}
            onChange={(e) => updateField("status", e.target.value)}
          >
            <option value="draft">Draft</option>
            <option value="registration_open">Registration Open</option>
            <option value="round_live">Round Live</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
          </select>
        </label>

        <label className={cls.field}>
          <span className={cls.label}>Max Rounds</span>
          <input
            className={cls.input}
            type="number"
            min="1"
            max="20"
            value={form.maxRounds}
            onChange={(e) => updateField("maxRounds", e.target.value)}
          />
        </label>

        <label className={cls.field}>
          <span className={cls.label}>Avatar Set Key</span>
          <input
            className={cls.input}
            type="text"
            placeholder="default"
            value={form.avatarSetKey}
            onChange={(e) => updateField("avatarSetKey", e.target.value)}
          />
        </label>

        <label className={cls.field}>
          <span className={cls.label}>Coin Cost Per Loss</span>
          <input
            className={cls.input}
            type="number"
            min="0"
            value={form.settings.coinCostPerLoss}
            onChange={(e) => updateSetting("coinCostPerLoss", e.target.value)}
          />
        </label>
      </div>

      <div className={cls.optionRow}>
        <label className={cls.toggleCard}>
          <div className={cls.toggleText}>
            <span className={cls.toggleTitle}>Allow Selfie Upload</span>
            <span className={cls.toggleSub}>
              Players can upload a selfie during profile setup.
            </span>
          </div>

          <span className={cls.switch}>
            <input
              type="checkbox"
              checked={form.allowSelfieUpload}
              onChange={(e) =>
                updateField("allowSelfieUpload", e.target.checked)
              }
            />
            <span className={cls.slider} />
          </span>
        </label>

        <label className={cls.toggleCard}>
          <div className={cls.toggleText}>
            <span className={cls.toggleTitle}>Disable Coins After Round 1</span>
            <span className={cls.toggleSub}>
              Prevent coin deduction after the first round.
            </span>
          </div>

          <span className={cls.switch}>
            <input
              type="checkbox"
              checked={form.settings.afterRound1CoinsDisabled}
              onChange={(e) =>
                updateSetting("afterRound1CoinsDisabled", e.target.checked)
              }
            />
            <span className={cls.slider} />
          </span>
        </label>
      </div>

      <div className={cls.footer}>
        <div className={cls.note}>
          Secure room setup for the Noty Universe game system.
        </div>

        <button className={cls.submitBtn} type="submit" disabled={isDisabled}>
          <span className={cls.btnGlow} aria-hidden="true" />
          {loading ? "Creating..." : "Create Game"}
        </button>
      </div>
    </form>
  );
}
